import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Mail, Lock } from "lucide-react";

export default function AdminLogin() {
  usePageMeta("Admin Login", "Secure admin login for Nicee Eggs.");
  const { adminLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin({ email, password });
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-3xl bg-white p-8 shadow-lift">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand text-ink">
          <ShieldCheck className="h-6 w-6" />
        </span>
        <h1 className="mt-5 text-center font-heading text-2xl font-bold text-ink">Admin Access</h1>
        <p className="mt-1 text-center text-sm text-ink-soft">Secure dashboard login</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-ink-soft">Email</Label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@email.com" className="rounded-xl pl-9" required data-testid="admin-email" />
            </div>
          </div>
          <div>
            <Label className="text-ink-soft">Password</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="rounded-xl pl-9" required data-testid="admin-password" />
            </div>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="admin-login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in to Dashboard"}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
